import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Img from "@/components/Img";
import { BRANDS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

import { APP_IDS } from "@/data/content";
const APP_FILTERS = APP_IDS;
const chip = (active) =>
  `px-4 py-2 text-[11px] uppercase tracking-[0.14em] border transition-colors ${active ? "border-[#C2A675] text-[#C2A675]" : "border-white/15 text-[#A8A6A0] hover:border-white/40 hover:text-white"}`;

export default function Marcas() {
  const { t, lp } = useLanguage();
  const [filter, setFilter] = useState(null);
  const visible = useMemo(() => (filter ? BRANDS.filter((b) => b.apps.includes(filter)) : BRANDS), [filter]);

  return (
    <div data-testid="brands-page" className="pt-20">
      <Seo title={t("seo.brands.title")} description={t("seo.brands.desc")} />

      <section className="pt-16 lg:pt-20 pb-20 lg:pb-24 max-w-7xl mx-auto px-6">
        <Reveal className="mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:items-end">
          <div className="lg:col-span-7">
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("brands_page.tag")}</span>
            <h1 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4">{t("brands_page.title")}</h1>
          </div>
          <p className="lg:col-span-5 text-base text-[#A8A6A0] font-light leading-relaxed">{t("brands_page.sub")}</p>
        </Reveal>

        <Reveal delay={0.1} className="flex flex-wrap gap-3 mb-12 border-y border-white/10 py-5" data-testid="brand-filters">
          <button type="button" onClick={() => setFilter(null)} data-testid="brand-filter-all" className={chip(!filter)}>{t("brands_page.filter_all")}</button>
          {APP_FILTERS.map((f) => (
            <button key={f} type="button" onClick={() => setFilter(filter === f ? null : f)} data-testid={`brand-filter-${f}`} className={chip(filter === f)}>
              {t(`app_cats.${f}`)}
            </button>
          ))}
        </Reveal>

        <div className="space-y-14 lg:space-y-20">
          {visible.map((b, i) => {
            const idx = BRANDS.indexOf(b);
            return (
              <Reveal key={b.slug}>
                <article data-testid={`brand-section-${b.slug}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <Link to={lp(`/marcas/${b.slug}`)} className="lg:col-span-7 group block img-frame border border-white/10 aspect-[16/10]" data-testid={`brand-image-${b.slug}`}>
                    <Img id={b.image} alt={b.name} label={t("common.pending_image")} className="absolute inset-0 w-full h-full object-cover img-tone" />
                    <span className="absolute top-5 left-5 serif-display text-5xl sm:text-6xl font-light text-white/90 leading-none [text-shadow:0_2px_28px_rgba(0,0,0,0.75)]" aria-hidden="true">0{idx + 1}</span>
                  </Link>
                  <div className="lg:col-span-5">
                    <span className="mono-tag text-[10px] text-[#C2A675] uppercase tracking-[0.2em] inline-block mb-4">{t(`brand.${b.slug}.tag`)}</span>
                    {b.exclusive && <p className="text-[11px] uppercase tracking-[0.16em] text-[#E8E6E1] border-l-2 border-[#C2A675] pl-3 mb-4">{t("common.exclusive_pt")}</p>}
                    {b.logo && <img src={b.logo} alt={`${b.name} — logótipo oficial`} className="h-8 w-auto mb-3 object-contain object-left" loading="lazy" decoding="async" />}
                    <h2 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-2">{b.name}</h2>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#A8A6A0] mb-6">{t(`brand.${b.slug}.origin`)}</p>
                    <div className="mb-6 space-y-3">
                      {t(`brand.${b.slug}.desc`).map((p, k) => (
                        <p key={k} className="text-base text-[#A8A6A0] font-light leading-relaxed">{p}</p>
                      ))}
                    </div>
                    <ul className="flex flex-wrap gap-2 mb-8">
                      {b.apps.map((a) => (
                        <li key={a} className="px-3 py-1 border border-white/10 text-[10px] uppercase tracking-[0.14em] text-[#D6D3CC]">{t(`app_cats.${a}`)}</li>
                      ))}
                    </ul>
                    <Link to={lp(`/marcas/${b.slug}`)} data-testid={`brand-cta-${b.slug}`} className="btn-ghost px-7 py-3.5">
                      {t("brands_page.cta")} <ArrowRight size={14} aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
